import { useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import {
  FileText,
  Upload,
  Download,
  Trash2,
  MoreHorizontal,
  Search,
  X,
  RefreshCw,
  AlertTriangle,
  Loader2,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  trackingApi,
  ShipmentDocument,
  ShipmentDocType,
  UploadDocumentPayload,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

// ============================================
// Constants & Configuration
// ============================================

const DOCUMENT_TYPE_CONFIG: Record<
  ShipmentDocType,
  { label: string; icon: string; color: string }
> = {
  BILL_OF_LADING: { label: 'Bill of Lading', icon: '📋', color: 'text-status-info' },
  PROOF_OF_DELIVERY: { label: 'Proof of Delivery', icon: '✅', color: 'text-status-success' },
  COMMERCIAL_INVOICE: { label: 'Commercial Invoice', icon: '📄', color: 'text-status-info' },
  PACKING_LIST: { label: 'Packing List', icon: '📦', color: 'text-status-warning-foreground' },
  CUSTOMS_DECLARATION: {
    label: 'Customs Declaration',
    icon: '🛂',
    color: 'text-status-info-foreground',
  },
  INSURANCE_CERTIFICATE: {
    label: 'Insurance Certificate',
    icon: '🛡️',
    color: 'text-status-info-foreground',
  },
  CARRIER_BOOKING: { label: 'Carrier Booking', icon: '🚢', color: 'text-teal-600' },
  DELIVERY_RECEIPT: { label: 'Delivery Receipt', icon: '📝', color: 'text-lime-600' },
  PHOTO_DAMAGE_REPORT: {
    label: 'Photo/Damage Report',
    icon: '📷',
    color: 'text-status-destructive',
  },
  OTHER: { label: 'Other', icon: '📄', color: 'text-status-neutral' },
};

const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
];

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

// ============================================
// Form Schema
// ============================================

const uploadFormSchema = z.object({
  documentType: z.nativeEnum(ShipmentDocType, {
    required_error: 'Please select a document type',
  }),
  notes: z.string().optional(),
});

type UploadFormData = z.infer<typeof uploadFormSchema>;

// ============================================
// Helper Functions
// ============================================

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

function formatDate(date: Date): string {
  return format(date, 'MMM d, yyyy HH:mm');
}

function getDocumentTypeLabel(type: ShipmentDocType): string {
  return DOCUMENT_TYPE_CONFIG[type]?.label || type;
}

function getDocumentTypeIcon(type: ShipmentDocType): string {
  return DOCUMENT_TYPE_CONFIG[type]?.icon || '📄';
}

// ============================================
// Main Component
// ============================================

interface DocumentsPanelProps {
  shipmentId: string;
  className?: string;
}

export function DocumentsPanel({ shipmentId, className }: DocumentsPanelProps) {
  const queryClient = useQueryClient();

  // State
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<ShipmentDocument | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<ShipmentDocType | 'ALL'>('ALL');
  const [uploaderFilter, setUploaderFilter] = useState<string>('ALL');
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form
  const form = useForm<UploadFormData>({
    resolver: zodResolver(uploadFormSchema),
    defaultValues: {
      notes: '',
    },
  });

  // React Query - Fetch documents
  const {
    data: documents,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['documents', shipmentId],
    queryFn: () => trackingApi.documents.getDocuments(shipmentId),
    enabled: !!shipmentId,
    refetchInterval: 300000, // Refresh every 5 minutes
    staleTime: 60000, // Consider data fresh for 1 minute
  });

  // React Query - Upload mutation
  const uploadMutation = useMutation({
    mutationFn: (payload: UploadDocumentPayload) =>
      trackingApi.documents.uploadDocument(shipmentId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', shipmentId] });
      queryClient.invalidateQueries({ queryKey: ['tracking-shipment', shipmentId] });
      toast.success('Document uploaded successfully');
      setUploadDialogOpen(false);
      setFile(null);
      form.reset();
    },
    onError: (error) => {
      toast.error(`Upload failed: ${getErrorMessage(error)}`);
    },
  });

  // React Query - Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (docId: string) => trackingApi.documents.deleteDocument(shipmentId, docId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', shipmentId] });
      toast.success('Document deleted');
      setDeleteDialogOpen(false);
      setSelectedDocument(null);
    },
    onError: (error) => {
      toast.error(`Delete failed: ${getErrorMessage(error)}`);
    },
  });

  // Filtering logic
  const filteredDocuments = useMemo(() => {
    if (!documents) return [];

    return documents.filter((doc) => {
      // Search filter
      if (searchQuery && !doc.documentName.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }

      // Type filter
      if (typeFilter !== 'ALL' && doc.documentType !== typeFilter) {
        return false;
      }

      // Uploader filter
      if (uploaderFilter !== 'ALL' && doc.uploadedById !== uploaderFilter) {
        return false;
      }

      return true;
    });
  }, [documents, searchQuery, typeFilter, uploaderFilter]);

  // Group documents by type
  const groupedDocuments = useMemo(() => {
    const groups = filteredDocuments.reduce(
      (acc, doc) => {
        const type = doc.documentType;
        if (!acc[type]) acc[type] = [];
        acc[type].push(doc);
        return acc;
      },
      {} as Record<ShipmentDocType, ShipmentDocument[]>
    );

    return Object.entries(groups).map(([type, docs]) => ({
      type: type as ShipmentDocType,
      documents: docs.sort((a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime()),
    }));
  }, [filteredDocuments]);

  // Get unique uploaders for filter
  const uploaders = useMemo(() => {
    if (!documents) return [];
    const uniqueUploaders = new Map();
    documents.forEach((doc) => {
      if (!uniqueUploaders.has(doc.uploadedById)) {
        uniqueUploaders.set(doc.uploadedById, doc.uploadedBy.fullName);
      }
    });
    return Array.from(uniqueUploaders.entries()).map(([id, name]) => ({ id, name }));
  }, [documents]);

  // File validation
  const validateFile = (selectedFile: File): boolean => {
    if (!ALLOWED_FILE_TYPES.includes(selectedFile.type)) {
      toast.error('Invalid file type. Allowed: PDF, JPG, PNG, XLSX');
      return false;
    }

    if (selectedFile.size > MAX_FILE_SIZE) {
      toast.error('File size exceeds 25MB limit');
      return false;
    }

    return true;
  };

  // File selection handlers
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && validateFile(selectedFile)) {
      setFile(selectedFile);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && validateFile(droppedFile)) {
      setFile(droppedFile);
    }
  };

  // Download handler
  const handleDownload = async (doc: ShipmentDocument) => {
    try {
      const blob = await trackingApi.documents.downloadDocument(shipmentId, doc.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.documentName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Document downloaded');
    } catch (error) {
      toast.error(`Download failed: ${getErrorMessage(error)}`);
    }
  };

  // Upload form submission
  const onUploadSubmit = (data: UploadFormData) => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    uploadMutation.mutate({
      file,
      documentType: data.documentType,
      notes: data.notes,
    });
  };

  // Delete confirmation
  const handleDeleteConfirm = () => {
    if (selectedDocument) {
      deleteMutation.mutate(selectedDocument.id);
    }
  };

  // Document count
  const documentCount = documents?.length || 0;

  // ============================================
  // Render
  // ============================================

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
        <h3 className="text-base font-semibold mb-2">Failed to load documents</h3>
        <p className="text-sm text-muted-foreground mb-4">{getErrorMessage(error)}</p>
        <Button onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-foreground">
              <FileText className="h-5 w-5 text-primary" />
              Documents
              {documentCount > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {documentCount}
                </Badge>
              )}
            </h3>
            <div className="flex items-center gap-2">
              <Button onClick={() => setUploadDialogOpen(true)}>
                <Upload className="h-4 w-4 mr-2" />
                Upload
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Filter Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Dismiss"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setSearchQuery('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            <Select
              value={typeFilter}
              onValueChange={(value) => setTypeFilter(value as ShipmentDocType | 'ALL')}
            >
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Types</SelectItem>
                {Object.entries(DOCUMENT_TYPE_CONFIG).map(([type, config]) => (
                  <SelectItem key={type} value={type}>
                    {config.icon} {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {uploaders.length > 1 && (
              <Select value={uploaderFilter} onValueChange={setUploaderFilter}>
                <SelectTrigger className="w-full md:w-[200px]">
                  <SelectValue placeholder="All Uploaders" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Uploaders</SelectItem>
                  {uploaders.map((uploader) => (
                    <SelectItem key={uploader.id} value={uploader.id}>
                      {uploader.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Button
              variant="outline"
              size="icon"
              aria-label="Refresh documents"
              onClick={() => refetch()}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Document List */}
      <Card>
        <ScrollArea className="h-[600px]">
          <div className="space-y-4 p-4">
            {filteredDocuments.length === 0 ? (
              // Empty State
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <div className="h-24 w-24 rounded-full bg-muted flex items-center justify-center mb-4">
                  <FileText className="h-12 w-12 text-muted-foreground" />
                </div>
                <h3 className="text-base font-semibold mb-2">
                  {documentCount === 0 ? 'No Documents Yet' : 'No Matching Documents'}
                </h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                  {documentCount === 0
                    ? 'Upload your first document to get started. You can add BOL, POD, invoices, and other shipment-related documents.'
                    : 'Try adjusting your search or filter criteria.'}
                </p>
                {documentCount === 0 && (
                  <Button onClick={() => setUploadDialogOpen(true)}>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload First Document
                  </Button>
                )}
              </div>
            ) : (
              // Grouped Document Cards
              groupedDocuments.map(({ type, documents: typeDocs }) => (
                <div key={type} className="space-y-3">
                  {/* Group Header */}
                  <div className="flex items-center gap-2 px-2">
                    <div className="text-2xl">{getDocumentTypeIcon(type)}</div>
                    <h3 className="text-base font-semibold text-muted-foreground uppercase tracking-wide">
                      {getDocumentTypeLabel(type)} ({typeDocs.length})
                    </h3>
                  </div>

                  {/* Document Cards */}
                  <div className="space-y-2">
                    {typeDocs.map((doc) => (
                      <Card key={doc.id} className="hover:shadow-raised-hover transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            {/* Icon/Thumbnail */}
                            <div className="flex-shrink-0">
                              {doc.mimeType.startsWith('image/') ? (
                                <img
                                  src={doc.documentUrl}
                                  alt={doc.documentName}
                                  className="h-16 w-16 rounded object-cover"
                                />
                              ) : (
                                <div className="h-16 w-16 rounded bg-muted flex items-center justify-center">
                                  <FileText className="h-8 w-8 text-muted-foreground" />
                                </div>
                              )}
                            </div>

                            {/* Document Info */}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{doc.documentName}</p>
                              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                <span>{formatFileSize(doc.documentSize)}</span>
                                <span>•</span>
                                <span>Uploaded by {doc.uploadedBy.fullName}</span>
                                <span>•</span>
                                <span>{formatDate(doc.uploadedAt)}</span>
                              </div>
                              {doc.notes && (
                                <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                                  Notes: {doc.notes}
                                </p>
                              )}
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2">
                              <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)}>
                                <Download className="h-4 w-4 mr-1" />
                                Download
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" aria-label="More actions">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleDownload(doc)}>
                                    <Download className="h-4 w-4 mr-2" />
                                    Download
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-destructive"
                                    onClick={() => {
                                      setSelectedDocument(doc);
                                      setDeleteDialogOpen(true);
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>
              Upload supporting documents for this shipment. Maximum file size: 25MB.
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onUploadSubmit)} className="space-y-4">
              {/* Drag-and-Drop Upload Zone */}
              <div
                className={cn(
                  'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
                  isDragging
                    ? 'border-status-info-border bg-status-info-subtle'
                    : file
                      ? 'border-status-success-border bg-status-success-subtle'
                      : 'border-border hover:border-border'
                )}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls"
                  onChange={handleFileSelect}
                />

                {!file ? (
                  <>
                    <Upload className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
                    <p className="text-lg font-medium mb-2">
                      {isDragging ? 'Drop file here' : 'Drag and drop file here'}
                    </p>
                    <p className="text-sm text-muted-foreground mb-4">or click to browse</p>
                    <p className="text-xs text-muted-foreground">
                      Supported: PDF, JPG, PNG, XLSX (Max 25MB)
                    </p>
                  </>
                ) : (
                  <div className="flex items-center justify-center gap-4">
                    <FileText className="h-12 w-12 text-status-success" />
                    <div className="text-left">
                      <p className="font-medium">{file.name}</p>
                      <p className="text-sm text-muted-foreground">{formatFileSize(file.size)}</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                )}
              </div>

              {/* Document Type Select */}
              <FormField
                control={form.control}
                name="documentType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Document Type *</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select document type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(DOCUMENT_TYPE_CONFIG).map(([type, config]) => (
                          <SelectItem key={type} value={type}>
                            {config.icon} {config.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Notes Field */}
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Add any relevant notes about this document..."
                        className="resize-none"
                        rows={3}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Add context or special instructions for this document
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Info Box */}
              <div className="bg-status-info-subtle border border-status-info-border rounded-lg p-3 flex gap-2">
                <Info className="h-5 w-5 text-status-info flex-shrink-0" />
                <div className="text-sm text-status-info">
                  <p className="font-medium mb-1">Document Guidelines:</p>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Ensure document is clear and legible</li>
                    <li>Original signed copies preferred for BOL/POD</li>
                    <li>Color scans accepted for photos and damage reports</li>
                  </ul>
                </div>
              </div>

              {/* Dialog Footer */}
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setUploadDialogOpen(false)}
                  disabled={uploadMutation.isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={!file || uploadMutation.isPending}>
                  {uploadMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-semibold">{selectedDocument?.documentName}</span>? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
